<template>
	<div class="left-menu" :class="[small ? 'small' : '']">
		<div class="left-search">
			<p class="txt">{{ facilityName }}</p>
			<button class="btn-search" @click="pop = true"></button>
		</div>
		<div class="pump-name web-hide">
			<p>{{ selectedTerminal?.pumpModelName ?? "" }}</p>
		</div>

		<!-- TM List -->
		<div class="package-list">
			<ul v-if="currentTerminals.length">
				<li
					v-for="terminal in currentTerminals"
					:key="terminal.id"
					:class="{ on: selectedTerminalId === terminal.id }"
					@click="selectTerminal(terminal.id, terminal.deviceId)"
					>
					<i class="ico"></i>
					<!-- pumpAlias 값이 있을 때만 <p> 렌더링 -->
					<!-- 툴팁 내용이 있을 때 -->
					<template v-if="terminal.pumpAliasTooltip">
						<p v-tooltip:bottom="terminal.pumpAliasTooltip">
							{{ terminal.pumpAlias }}
						</p>
					</template>
					<!-- 툴팁 내용이 없을 때 -->
					<template v-else>
						<p>
							{{ terminal.pumpAlias }}
						</p>
					</template>
				</li>
			</ul>
		</div>

		<div class="left-logout">
			<button class="btn-logout" @click="logout">{{ lang.button('logout') }}</button>
			<button class="btn-mini" @click="small = !small"></button>
		</div>
	</div>

	<div class="popup-wrap" v-if="pop">
		<placeSearchPop @closePop="pop = false"></placeSearchPop>
	</div>
</template>

<script setup lang="ts">
import {ref, onMounted, watch, computed} from "vue";
import {useRoute} from "nuxt/app";
import placeSearchPop from '../components/placeSearchPop.vue'
import {useAuth} from "~/composables/useAuth";
import {useTerminal} from "~/composables/useTerminal";
import {useFacility} from "~/composables/useFacility";
import {useHandleCode} from "~/composables/useHandleCode";
import {ERROR_TYPE} from "~/constants/errorType";
import {useLanguage} from "~/composables/useLanguage";

const {lang} = useLanguage();
const {logout} = useAuth();
const pop = ref(false);
const route = useRoute();

const { fireTerminals, waterTerminals, drainageTerminals, selectedTerminalDeviceId, selectedTerminal } = useTerminal();
const { facilityId, facilityName, handleFacilityTableData } = useFacility();
const { handleErrorCode } = useHandleCode();

const selectedTerminalId = ref<number | null>(null);
const isTerminalDataLoaded = ref(false);

const small = ref(false);

// Route에 따라 터미널 리스트를 선택
const currentTerminals = computed(() => {
	if (route.path.includes("fire_page")) return fireTerminals.value;
	if (route.path.includes("water_page")) return waterTerminals.value;
	if (route.path.includes("drainage_page")) return drainageTerminals.value;
	return [];
});

// 데이터가 로드된 후에만 에러메세지를 호출하도록 개선
function updateSelectedTerminal() {
	if (!isTerminalDataLoaded.value) return;

	if (currentTerminals.value.length > 0) {
		if(selectedTerminalDeviceId.value === null) {
			selectedTerminalId.value = currentTerminals.value[0].id;
			selectedTerminalDeviceId.value = currentTerminals.value[0].deviceId;
		} else {
			selectedTerminalId.value = currentTerminals.value.find(terminal => terminal.deviceId === selectedTerminalDeviceId.value)?.id || null;
		}
	} else {
		handleErrorCode(ERROR_TYPE.NO_DEVICE_ID);
		selectedTerminalId.value = null;
		selectedTerminalDeviceId.value = null;
	}
}

// 데이터를 로드하고 완료 후 상태를 업데이트하는 로직 예시
async function fetchTerminalData() {
	isTerminalDataLoaded.value = false;

	await handleFacilityTableData(); // 이 메소드가 fire/water/drainage 터미널들을 API로 가져오는 로직 포함

	isTerminalDataLoaded.value = true;
	updateSelectedTerminal();
}

onMounted(async () => {
	await fetchTerminalData();
});

// 페이지 변경 시에도 데이터 다시 로드
watch(
	() => route.path,
	async () => {
		await fetchTerminalData();
	}
);

// 시설 ID가 바뀌었을 때도 다시 데이터를 로드
watch(facilityId, async () => {
	await fetchTerminalData();
	selectedTerminalDeviceId.value = null; // 터미널이 변경될 때마다 초기화
	updateSelectedTerminal();
});

function selectTerminal(terminalId: number, deviceId: number) {
	selectedTerminalId.value = terminalId;
	selectedTerminalDeviceId.value = deviceId;
}
</script>